import React, { useState, useEffect } from 'react'
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Stack,
  CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { ArrowRightLongIcon, CustomEditIcon } from '../Icons'
import PDFViewer from '../PDFViewer/PDFViewer'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { FilterSelect } from './FilterSelect'

export interface SelectedDocument {
  id: string
  name: string
  // pageRanges?: { startPage: number; endPage: number }[]
  fromPage?: number
  toPage?: number
  allPages: boolean
}
interface DocumentDrawerProps {
  open: boolean
  onClose: () => void
  onDocumentSelect: (
    documentIds: SelectedDocument[],
    labelText?: string,
  ) => void
  onDelete?: () => void // Callback to delete entire badge from editor
  selectedDocumentIds?: SelectedDocument[]
  initialLabel?: string
  yearOptions: string[]
  moduleOptions: string[]
  topicOptions: string[]
  documents: TDocument[]
}

export interface TDocument {
  breadcrumbs: Array<{ knowledge_group_id: string; name: string }>
  file_id: string
  file_name: string
  name: string
  _id: string
  knowledge_group_1_name: string
  knowledge_group_2_name: string
  knowledge_group_3_name?: string
}

export const DocumentDrawer: React.FC<DocumentDrawerProps> = ({
  open,
  onClose,
  onDocumentSelect,
  onDelete,
  selectedDocumentIds,
  initialLabel,
  yearOptions,
  moduleOptions,
  topicOptions,
  documents,
}) => {
  const { t } = useTranslation()
  const [filteredDocuments, setFilteredDocuments] = useState<TDocument[]>([])

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedYear, setSelectedYear] = useState<string[]>([])
  const [selectedModule, setSelectedModule] = useState<string[]>([])
  const [selectedTopic, setSelectedTopic] = useState<string[]>([])

  const [selectedDocuments, setSelectedDocuments] = useState<
    SelectedDocument[]
  >([])
  const [badgeLabel, setBadgeLabel] = useState<string>('')

  // Determine if we're in edit mode (when selectedDocumentIds exist)
  const isEditMode = !!(selectedDocumentIds && selectedDocumentIds.length > 0)
  const [selectedPdfDocumentId, setSelectedPdfDocumentId] = useState<string>('')
  const [selectedPdfDocumentName, setSelectedPdfDocumentName] =
    useState<string>('')
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState<boolean>(false)

  const [pageRangesError, setPageRangesError] = useState<boolean>(false)
  const [zeroPageError, setZeroPageError] = useState<boolean>(false)

  const [pageRanges, setPageRanges] = useState<
    { startPage: number; endPage: number }[]
  >([{ startPage: 1, endPage: 1 }])
  const [totalPages, setTotalPages] = useState<number | null>(null)

  const selectedIndividualPages = pageRanges
    .slice(0, -1)
    .reduce((acc: number[], range) => {
      const pages = Array.from(
        { length: range.endPage - range.startPage + 1 },
        (_, i) => range.startPage + i,
      )
      return [...acc, ...pages]
    }, [])

  useEffect(() => {
    if (pageRanges.length === 0) return

    const lastRange = pageRanges[pageRanges.length - 1]

    const isZeroPageRange = pageRanges.some(range => !range.startPage)

    if (isZeroPageRange) {
      setZeroPageError(true)
    } else {
      setZeroPageError(false)
    }

    const pagesSelected = Array.from(
      { length: lastRange.endPage - lastRange.startPage + 1 },
      (_, i) => lastRange.startPage + i,
    )

    const isOverlapRange = selectedIndividualPages.some(page =>
      pagesSelected.includes(page),
    )

    if (isOverlapRange) {
      setPageRangesError(true)
    } else {
      setPageRangesError(false)
    }
  }, [pageRanges, selectedIndividualPages])

  useEffect(() => {
    setFilteredDocuments(documents)
  }, [documents])

  useEffect(() => {
    if (open) {
      setSelectedDocuments(selectedDocumentIds || [])
      setBadgeLabel(initialLabel || '')
    } else {
      setSearchQuery('')
      setSelectedYear([])
      setSelectedModule([])
      setSelectedTopic([])
      setBadgeLabel('')

      setSelectedPdfDocumentId('')
      setIsPdfViewerOpen(false)
    }
  }, [open, initialLabel, selectedDocumentIds])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
  }

  useEffect(() => {
    setFilteredDocuments(prev => {
      const filtered = documents.filter(doc => {
        const isYearMatch =
          selectedYear.length === 0 ||
          selectedYear.includes(doc.knowledge_group_1_name)
        const isModuleMatch =
          selectedModule.length === 0 ||
          selectedModule.includes(doc.knowledge_group_2_name)
        const isTopicMatch =
          selectedTopic.length === 0 ||
          selectedTopic.includes(doc.knowledge_group_3_name)

        const isSearchMatch =
          searchQuery.trim() === '' ||
          doc.name.toLowerCase().includes(searchQuery.toLowerCase())

        return isYearMatch && isModuleMatch && isTopicMatch && isSearchMatch
      })
      return filtered
    })
  }, [selectedYear, selectedModule, selectedTopic, searchQuery, documents])

  const handleCheckboxChange = (selectedDocument: TDocument) => {
    setSelectedDocuments(prev => {
      if (prev.map(document => document.id).includes(selectedDocument._id)) {
        return prev.filter(id => id.id !== selectedDocument._id)
      } else {
        return [
          ...prev,
          {
            id: selectedDocument._id,
            name: selectedDocument.name,
            allPages: true,
          },
        ]
      }
    })
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDocuments(
        filteredDocuments.map(doc => ({
          id: doc._id,
          name: doc.name,
          allPages: true,
        })),
      )
    } else {
      setSelectedDocuments([])
    }
  }

  const handleOpenPdfViewer = (doc: TDocument) => {
    setSelectedPdfDocumentId(doc._id)
    setSelectedPdfDocumentName(doc.name)
    setIsPdfViewerOpen(true)

    const selectedDocument = selectedDocuments.find(item => item.id === doc._id)

    if (!selectedDocument || selectedDocument.allPages) {
      setPageRanges([{ startPage: 1, endPage: totalPages }])
    }
  }

  const handleClosePdfViewer = () => {
    setIsPdfViewerOpen(false)
    setSelectedPdfDocumentId('')
    setSelectedPdfDocumentName('')
    setPageRanges([{ startPage: 1, endPage: 1 }])
    setTotalPages(null)
  }

  const handlePdfLoaded = (pageCount: number) => {
    setTotalPages(pageCount)
    const allSelectedDocument = selectedDocuments.filter(
      item => item.id === selectedPdfDocumentId,
    )
    const selectedPageRanges =
      allSelectedDocument.length > 0
        ? allSelectedDocument.map(item =>
            item.allPages
              ? { startPage: 1, endPage: pageCount }
              : {
                  startPage: item.fromPage,
                  endPage: item.toPage,
                },
          )
        : [{ startPage: 1, endPage: pageCount }]
    setPageRanges(selectedPageRanges)
  }

  const handleStartPageChange = (value: string, index: number) => {
    const numValue = Number(value)
    if (numValue >= 0 && numValue <= totalPages) {
      setPageRanges(prev => {
        const updatedRanges = [...prev]
        updatedRanges[index].startPage = numValue
        return updatedRanges
      })
    }
  }

  const handleEndPageChange = (value: string, index: number) => {
    const numValue = Number(value)
    if (numValue >= 0 && numValue <= totalPages) {
      setPageRanges(prev => {
        const updatedRanges = [...prev]
        updatedRanges[index].endPage = numValue
        return updatedRanges
      })
    }
  }

  const handleAddPageRange = () => {
    setPageRanges(prev => {
      const aboveEndPage = prev[prev.length - 1].endPage
      const newStartPage = aboveEndPage === totalPages ? 1 : aboveEndPage + 1

      return [...prev, { startPage: newStartPage, endPage: totalPages }]
    })
  }

  const handleRemovePageRange = (index: number) => {
    setPageRanges(prev => {
      const updatedRanges = [...prev]
      updatedRanges.splice(index, 1)
      return updatedRanges
    })
  }

  const isAllSelected =
    filteredDocuments.length > 0 &&
    selectedDocuments.length === filteredDocuments.length

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ overflow: 'hidden' }}
      slotProps={{
        paper: { sx: { width: { xs: '100%', sm: 400, md: 560 }, p: 0 } },
      }}
    >
      <Box
        sx={{
          p: 2,
          ...(isPdfViewerOpen
            ? {}
            : {
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
              }),
        }}
      >
        {isPdfViewerOpen ? (
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <IconButton onClick={handleClosePdfViewer} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle2" component="p">
              {selectedPdfDocumentName}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" component="p">
                {t('Link References')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {t(
                  'Select the document(s) you’d like to reference for this part of the scenario. The AI will only consider the chosen materials when responding to the task assigned.',
                )}
              </Typography>
            </Box>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        {/* Name Field - Only show in edit mode */}
        {!isPdfViewerOpen && isEditMode && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('Name')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t('Enter custom name for this reference (optional)')}
              value={badgeLabel}
              onChange={e => setBadgeLabel(e.target.value)}
            />
          </Box>
        )}

        {!isPdfViewerOpen && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <FilterSelect
                options={yearOptions}
                value={selectedYear}
                onChange={value => {
                  setSelectedYear(value)
                }}
                placeholder={t('Year')}
              />
              <FilterSelect
                options={moduleOptions}
                value={selectedModule}
                onChange={value => {
                  setSelectedModule(value)
                }}
                placeholder={t('Module')}
              />
              <FilterSelect
                options={topicOptions}
                value={selectedTopic}
                onChange={value => {
                  setSelectedTopic(value)
                }}
                placeholder={t('Topic')}
              />
            </Box>

            <TextField
              placeholder={t('Search document(s)')}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {isPdfViewerOpen ? (
          <Box>
            <PDFViewer
              documentId={selectedPdfDocumentId}
              getNoOfPages={handlePdfLoaded}
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" component="p" gutterBottom>
                    {t('Limit pages')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '14px', fontWeight: 600 }}
                    gutterBottom
                  >
                    {t('Specify the pages to limit the document to.')}
                  </Typography>
                </Box>
                <Button
                  disabled={pageRangesError || zeroPageError}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none' }}
                  onClick={handleAddPageRange}
                >
                  {t('Add')}
                </Button>
              </Box>

              {totalPages === null ? (
                <Stack alignItems="center" sx={{ my: 3 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                pageRanges.map((range, index) => {
                  return (
                    <>
                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        key={index}
                        sx={{ mt: 2 }}
                      >
                        <Grid size={5}>
                          <TextField
                            error={range.startPage > range.endPage}
                            value={Number(range.startPage)}
                            type="number"
                            onChange={e =>
                              handleStartPageChange(e.target.value, index)
                            }
                            slotProps={{
                              htmlInput: { min: 0, max: totalPages },
                            }}
                          />
                        </Grid>
                        <Grid size={1} sx={{ textAlign: 'center' }}>
                          <ArrowRightLongIcon />
                        </Grid>
                        <Grid size={5}>
                          <TextField
                            error={range.startPage > range.endPage}
                            value={range.endPage}
                            onChange={e =>
                              handleEndPageChange(e.target.value, index)
                            }
                            type="number"
                            slotProps={{
                              htmlInput: {
                                min: range.startPage,
                                max: totalPages,
                              },
                            }}
                          />
                        </Grid>
                        {pageRanges.length > 1 && (
                          <Grid size={1} sx={{ textAlign: 'right' }}>
                            <IconButton
                              onClick={() => handleRemovePageRange(index)}
                            >
                              <DeleteOutlineIcon color="error" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                      {range.startPage > range.endPage && (
                        <Typography
                          color="error"
                          variant="body1"
                          sx={{ mt: 1, fontWeight: 400 }}
                        >
                          {t(
                            'Starting page must be lower than or equal the ending page.',
                          )}
                        </Typography>
                      )}
                      {!range.startPage && (
                        <Typography
                          color="error"
                          variant="body1"
                          sx={{ mt: 1, fontWeight: 400 }}
                        >
                          {t('Page numbers must start from 1.')}
                        </Typography>
                      )}
                      {pageRangesError && index === pageRanges.length - 1 && (
                        <Typography
                          color="error"
                          variant="body1"
                          sx={{ mt: 1, fontWeight: 400 }}
                        >
                          {t(
                            'Overlapping page ranges detected. Please adjust your selection. Each page can only be included in one range.',
                          )}
                        </Typography>
                      )}
                    </>
                  )
                })
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid #C4C3C4',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                px: 1,
                bgcolor: '#F8F7F8',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={
                      selectedDocuments.length > 0 &&
                      selectedDocuments.length < filteredDocuments.length
                    }
                  />
                }
                sx={{ m: 0, height: '40px' }}
                label={t('Select All')}
                slotProps={{
                  typography: {
                    variant: 'body1',
                    fontWeight: 600,
                    m: 0,
                  },
                }}
              />
            </Box>

            <List
              sx={{ maxHeight: 'calc(100vh - 386px)', overflow: 'auto' }}
              disablePadding
            >
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map(doc => {
                  const selectedDocument = selectedDocuments.find(
                    item => item.id === doc._id,
                  )

                  /** If amount of selected document > 1 => it has multiple selected page ranges */
                  const allSelectedDocuments = selectedDocuments.filter(
                    item => item.id === doc._id,
                  )

                  return (
                    <ListItem
                      key={doc._id}
                      sx={{
                        '&:hover': { backgroundColor: '#f0f7ff' },
                        display: 'flex',
                        padding: '8px',
                      }}
                    >
                      <Checkbox
                        checked={!!selectedDocument}
                        onChange={() => handleCheckboxChange(doc)}
                        sx={{ mr: 1 }}
                      />
                      <Box
                        sx={{
                          flexGrow: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <ListItemText
                          primary={doc.breadcrumbs
                            .map((item: any) => item.name)
                            .join(' / ')}
                          secondary={doc.name}
                          slotProps={{
                            primary: {
                              variant: 'caption',
                              color: 'text.secondary',
                              sx: { fontSize: '0.75rem' },
                            },
                            secondary: {
                              variant: 'body1',
                              color: 'text.primary',
                              sx: {
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                mt: 0.5,
                              },
                            },
                          }}
                        />

                        {selectedDocument && !selectedDocument.allPages && (
                          <Box
                            sx={{
                              width: '80px',
                              flexShrink: 0,
                              ml: 2,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '12px', color: '#6B7085' }}
                            >
                              {t('Pages')}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 400 }}
                            >
                              {selectedDocument.fromPage || 1} -{' '}
                              {selectedDocument.toPage || 1}
                              {allSelectedDocuments.length > 1 && (
                                <Typography
                                  variant="body1"
                                  component="span"
                                  sx={{
                                    fontWeight: 400,
                                    bgcolor: '#001DE0',
                                    color: '#fff',
                                    borderRadius: 2,
                                    px: 0.5,
                                    ml: 1,
                                  }}
                                >
                                  +{allSelectedDocuments.length - 1}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        )}

                        <IconButton onClick={() => handleOpenPdfViewer(doc)}>
                          <CustomEditIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  )
                })
              ) : (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: 'calc(100vh - 386px)' }}
                >
                  <Typography color="text.secondary">
                    No documents found
                  </Typography>
                </Stack>
              )}
            </List>
          </Box>
        )}

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            pt: 2,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          {isPdfViewerOpen ? (
            <>
              <Button variant="outlined" onClick={handleClosePdfViewer}>
                {t('Back')}
              </Button>
              <Button
                disabled={pageRangesError || zeroPageError}
                variant="contained"
                color="primary"
                onClick={() => {
                  let preventClosePdfViewer = false
                  setSelectedDocuments(prev => {
                    const updatedDocuments = prev.filter(
                      doc => doc.id !== selectedPdfDocumentId,
                    )

                    const isSelectAllPages =
                      pageRanges.length === 1 &&
                      pageRanges[0].endPage - pageRanges[0].startPage + 1 ===
                        totalPages

                    const newDocuments = pageRanges.map(pageRange => [
                      {
                        id: selectedPdfDocumentId,
                        name: selectedPdfDocumentName,
                        fromPage: pageRange.startPage || 1,
                        toPage: pageRange.endPage,
                        allPages: isSelectAllPages,
                      },
                    ])

                    const result = [...updatedDocuments, ...newDocuments.flat()]
                    const isInvalidResult = result.some(
                      item => item.fromPage > item.toPage,
                    )
                    if (isInvalidResult) {
                      preventClosePdfViewer = true
                      return prev
                    }
                    return result
                  })
                  if (!preventClosePdfViewer) {
                    handleClosePdfViewer()
                  }
                }}
              >
                {t('Limit Pages')}
              </Button>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {/* Delete button - only show when editing existing reference */}
              {selectedDocumentIds &&
              selectedDocumentIds.length > 0 &&
              onDelete ? (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    onDelete()
                    onClose()
                  }}
                >
                  {t('Delete')}
                </Button>
              ) : (
                <Box /> // Spacer
              )}

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={onClose}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectedDocuments.length === 0}
                  onClick={() => {
                    onDocumentSelect(selectedDocuments, badgeLabel)
                    onClose()
                  }}
                >
                  {t(
                    `Link Document${selectedDocuments.length !== 1 ? 's' : ''}`,
                  )}
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
